import React, { useState } from 'react';

interface ConfirmPurchaseButtonProps {
  purchase: any;
  onConfirmSuccess?: (purchase?: any) => void;
  onConfirmError?: (error: string) => void;
  label?: string;
  variant?: 'confirm' | 'receive';
}

const ConfirmPurchaseButton: React.FC<ConfirmPurchaseButtonProps> = ({ 
  purchase, 
  onConfirmSuccess,
  onConfirmError,
  label,
  variant = 'confirm'
}) => {
  const [isConfirming, setIsConfirming] = useState(false);

  const handleAction = async () => {
    if (variant === 'receive') {
      await markAsReceived();
    } else {
      await confirmPendingPurchase();
    }
  };

  const confirmPendingPurchase = async () => {
    try {
      setIsConfirming(true);
      
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      const purchaseId = purchase.pending_id || purchase.id;
      if (!purchaseId) {
        throw new Error('No purchase ID found for confirmation');
      }

      const response = await fetch(`https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/pending/${purchaseId}/confirm`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          pending_id: purchaseId,
          pending_purchase_id: purchaseId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to confirm purchase (Status: ${response.status}): ${errorText}`);
      }

      let resultData: any = null;
      try {
        resultData = await response.json();
      } catch (parseError) {
        // ignore JSON parse errors if the API returns empty body
      }

      const mergedPurchase =
        typeof resultData === 'object' && resultData !== null
          ? {
              ...purchase,
              ...(resultData.purchase || {}),
              ...resultData
            }
          : purchase;

      if (onConfirmSuccess) {
        onConfirmSuccess(mergedPurchase);
      } else {
        alert('Purchase confirmed successfully!');
      }
      
    } catch (error: any) {
      console.error('Error confirming purchase:', error);
      const errorMessage = error.message || 'Failed to confirm purchase';
      
      if (onConfirmError) {
        onConfirmError(errorMessage);
      } else {
        alert(`Failed to confirm purchase: ${errorMessage}`);
      }
    } finally {
      setIsConfirming(false);
    }
  };

  const markAsReceived = async () => {
    try {
      setIsConfirming(true);
      
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      // Resolve purchase ID - try multiple possible fields
      const purchaseId = purchase.purchase_id || purchase.purchaseId || purchase.id || purchase.pending_id;
      
      if (!purchaseId) {
        throw new Error('No purchase ID found for receive action');
      }

      const response = await fetch(`https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/${purchaseId}/receive`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          purchase_id: purchaseId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to mark purchase as received (Status: ${response.status}): ${errorText}`);
      }

      const resultData = await response.json();

      if (onConfirmSuccess) {
        onConfirmSuccess(resultData);
      } else {
        alert('Purchase marked as received successfully!');
      }
      
    } catch (error: any) {
      console.error('Error marking purchase as received:', error);
      // Enhanced CORS error handling
      let errorMessage = error.message || 'Failed to mark purchase as received';
      if (error.message && (error.message.includes('CORS') || error.message.includes('Failed to fetch') || 
          error.message.includes('Access-Control-Allow-Origin') || error.message.includes('network') ||
          error.message.includes('ERR_FAILED'))) {
        errorMessage = 'CORS Error: Unable to connect to the server. Please check your connection and ensure the server allows requests from this origin.';
      }
      
      if (onConfirmError) {
        onConfirmError(errorMessage);
      } else {
        alert(`Failed to mark purchase as received: ${errorMessage}`);
      }
    } finally {
      setIsConfirming(false);
    }
  };

  const purchaseId = purchase.pending_id || purchase.id;
  const isDisabled = isConfirming;
  const displayLabel = label || (variant === 'receive' ? 'Received' : 'Confirm');
  const palette = variant === 'receive'
    ? {
        activeGradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
        disabledGradient: 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
        hoverGradient: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
        hoverShadow: '0 4px 10px rgba(59, 130, 246, 0.4)',
        idleShadow: '0 2px 6px rgba(59, 130, 246, 0.3)'
      }
    : {
        activeGradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        disabledGradient: 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
        hoverGradient: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
        hoverShadow: '0 4px 10px rgba(16, 185, 129, 0.4)',
        idleShadow: '0 2px 6px rgba(16, 185, 129, 0.3)'
      };

  return (
    <button
      onClick={handleAction}
      disabled={isDisabled}
      style={{
        background: isDisabled ? palette.disabledGradient : palette.activeGradient,
        color: 'white',
        border: 'none',
        padding: '0.5rem 0.75rem',
        borderRadius: '6px',
        fontSize: '0.75rem',
        fontWeight: '600',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '0.375rem',
        opacity: isDisabled ? 0.7 : 1,
        boxShadow: isDisabled ? 'none' : palette.idleShadow
      }}
      onMouseEnter={(e) => {
        if (!isDisabled && e.currentTarget) {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = palette.hoverShadow;
          e.currentTarget.style.background = palette.hoverGradient;
        }
      }}
      onMouseLeave={(e) => {
        if (!isDisabled && e.currentTarget) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = palette.idleShadow;
          e.currentTarget.style.background = palette.activeGradient;
        }
      }}
    >
      {isConfirming ? (
        <>
          <div style={{
            width: '12px',
            height: '12px',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderTop: '2px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          {`${displayLabel}...`}
        </>
      ) : (
        <>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 12l2 2 4-4"/>
            <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
          </svg>
          {displayLabel}
        </>
      )}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </button>
  );
};

export default ConfirmPurchaseButton;

